// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect } from 'react'
import { Users, ShieldCheck, Copy, CheckCircle2, X, Trash2, AlertTriangle, ArrowRight, ArrowUpCircle } from 'lucide-react'
import { Card }         from '@/components/ui/Card'
import { InfoBlock }    from '@/components/ui/InfoBlock'
import { Button, Input, Select } from '@/components/ui'
import { Checkbox }     from '@/components/ui/Checkbox'
import { RadioGroup }   from '@/components/ui/RadioGroup'
import { Toggle }       from '@/components/ui/Toggle'
import { Badge }        from '@/components/ui/Badge'
import { useAuthStore } from '@/store'
import { useToast }     from '@/hooks/useToast'
import {
  loadTrustedPersons, createTrustedPerson, revokeTrustedPerson,
  type TrustedPersonConfig,
} from '@/services/trustedPerson'
import { promoteTrustedPerson } from '@/services/trustedPerson/promote'
import { loadPartner, loadChildren } from '@/services/wizard'
import { applyKnownPerson, fromPartner, composeFullName } from '@/lib/personReuse'
import { getFirstProfile, saveModuleProgress, loadOwnerDob } from '@/services/profile'
import { TrustedPersonRelationship, ActivationTrigger, TrustedPersonRole } from '@/types'

// ── Inline bevestigingsdialoog - vervangt window.confirm() (werkt niet op iOS PWA) ──
interface ConfirmDialogProps { message: string; onConfirm: () => void; onCancel: () => void }
function ConfirmDialog({ message, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 flex flex-col gap-4">
        <p className="text-sm text-ink leading-relaxed">{message}</p>
        <div className="flex gap-3">
          <Button variant="secondary" fullWidth onClick={onCancel}>Annuleren</Button>
          <Button fullWidth onClick={onConfirm}>Bevestigen</Button>
        </div>
      </div>
    </div>
  )
}
import type { SaveStatus } from '@/hooks/useAutosave'

// ── Reference data ─────────────────────────────────────────────────────────────

// Uitgebreide relatielijst - waarden zijn enum-strings voor bekende + vrije tekst voor granulaire opties
// Backward compat: bestaande vrije tekst wordt getoond via 'Overig' + vrij veld
const REL_OPTIONS = [
  { value: TrustedPersonRelationship.PARTNER,  label: 'Partner' },
  { value: TrustedPersonRelationship.CHILD,    label: 'Kind' },
  { value: 'VADER',                            label: 'Vader' },
  { value: 'MOEDER',                           label: 'Moeder' },
  { value: 'BROER',                            label: 'Broer' },
  { value: 'ZUS',                              label: 'Zus' },
  { value: 'OOM',                              label: 'Oom' },
  { value: 'TANTE',                            label: 'Tante' },
  { value: 'NEEF',                             label: 'Neef' },
  { value: 'NICHT',                            label: 'Nicht' },
  { value: TrustedPersonRelationship.SIBLING,  label: 'Broer of zus' },
  { value: TrustedPersonRelationship.FRIEND,   label: 'Vriend of vriendin' },
  { value: 'VRIEND',                           label: 'Vriend' },
  { value: 'VRIENDIN',                         label: 'Vriendin' },
  { value: 'BUURMAN',                          label: 'Buurman' },
  { value: 'BUURVROUW',                        label: 'Buurvrouw' },
  { value: 'COLLEGA',                          label: 'Collega' },
  { value: 'KENNIS',                           label: 'Kennis' },
  { value: TrustedPersonRelationship.OTHER,    label: 'Overig' },
]

// Bekende dropdown-waarden - alles buiten deze lijst is vrije tekst (backward compat)
const KNOWN_REL_VALUES = new Set(REL_OPTIONS.map(o => o.value))

const ACT_OPTIONS = [
  {
    value: ActivationTrigger.ON_DEATH,
    label: 'Pas na mijn overlijden (aanbevolen)',
    description: 'Uw vertrouwenspersoon krijgt pas toegang nadat uw overlijden is bevestigd.',
  },
  {
    value: ActivationTrigger.IMMEDIATE,
    label: 'Direct toegang geven',
    description: 'Uw vertrouwenspersoon kan direct inloggen. Geschikt wanneer u ondersteuning wilt bij uw administratie of voorbereiding.',
  },
]

const PERMS = [
  { key: 'moduleFuneralWishes',    label: 'Uitvaartwensen',        def: true },
  { key: 'modulePersonalFarewell', label: 'Persoonlijk afscheid',  def: false },
  { key: 'moduleMedicalWishes',    label: 'Medische wensen',       def: false },
  { key: 'moduleDocumentIndex',    label: 'Documentwijzer',        def: true },
  { key: 'moduleMessages',         label: 'Berichtenservice',      def: true },
  { key: 'moduleDigitalEstate',    label: 'Digitale nalatenschap', def: false },
  { key: 'moduleLifeStory',        label: 'Levensverhaal',         def: false },
]

// ── Types ──────────────────────────────────────────────────────────────────────

interface Form {
  firstName: string; lastName: string; relationship: string; email: string; phone: string
  activationTrigger: string
  moduleFuneralWishes: boolean; modulePersonalFarewell: boolean
  moduleMedicalWishes: boolean; moduleDocumentIndex: boolean
  moduleMessages: boolean; moduleDigitalEstate: boolean; moduleLifeStory: boolean
}

const EMPTY_FORM: Form = {
  firstName: '', lastName: '', relationship: '', email: '', phone: '',
  activationTrigger: ActivationTrigger.ON_DEATH,
  moduleFuneralWishes: true, modulePersonalFarewell: false,
  moduleMedicalWishes: false, moduleDocumentIndex: true,
  moduleMessages: true, moduleDigitalEstate: false, moduleLifeStory: false,
}

// Bekende personen uit eerder ingevulde stappen (rijk: alle beschikbare velden)
interface KnownPerson {
  label:        string          // weergavenaam in de keuzelijst
  firstName:    string
  lastName?:    string
  relationship: TrustedPersonRelationship
  dob?:         string          // V40C-P1: voor minderjarige-check + V49-UAT: overname
  phone?:       string
  email?:       string
}

// ── Component ──────────────────────────────────────────────────────────────────

export function NewStep01TrustedPerson({ onSaveStatus, onFormOpen, onLaterChosen, onVpCreated }: {
  onSaveStatus:  (s: SaveStatus) => void
  onFormOpen?:   (open: boolean) => void
  onLaterChosen?: () => void
  onVpCreated?:   () => void
}) {
  const { userId } = useAuthStore()
  const { toast }  = useToast()

  const [persons,      setPersons]      = useState<TrustedPersonConfig[]>([])
  const [laterChosen,  setLaterChosen]  = useState(false)
  const [knownPersons,  setKnownPersons]  = useState<KnownPerson[]>([])
  const [knownLoading,  setKnownLoading]  = useState(true)
  const [showForm,     setShowForm]     = useState(false)
  const [wizStep,      setWizStep]      = useState(1)
  const [form,         setForm]         = useState<Form>(EMPTY_FORM)
  const [isSaving,     setIsSaving]     = useState(false)
  const [genCode,      setGenCode]      = useState<string | null>(null)
  const [ackKeep,      setAckKeep]      = useState(false)  // V49: bewust bewaren
  const [ackTrust,     setAckTrust]     = useState(false)  // V49: vertrouwenshandeling / geen overlijdenscontrole
  const [copied,       setCopied]       = useState(false)
  const [dobHash,      setDobHash]      = useState('')
  const [ownerName,    setOwnerName]    = useState('')
  const [vpDob,        setVpDob]        = useState('')  // geboortedatum VP zelf - alleen voor leeftijdscheck
  // V40C-P1: minderjarige VP dialog
  const [minorWarning, setMinorWarning] = useState(false)   // dialog zichtbaar
  const [minorName,    setMinorName]    = useState('')       // naam voor in dialog
  const [minorPending, setMinorPending] = useState<KnownPerson | null>(null)
  const [showBrief,    setShowBrief]    = useState(false)
  const [briefForVP,   setBriefForVP]   = useState<TrustedPersonConfig | null>(null) // V41: brief vanuit bestaande kaart
  // Inline confirm - vervangt window.confirm() (iOS PWA compatibel)
  const [confirmRevoke,  setConfirmRevoke]  = useState<{ id: string; name: string } | null>(null)
  const [confirmPromote, setConfirmPromote] = useState<{ id: string; name: string } | null>(null)
  const [errors,       setErrors]       = useState<Partial<Form>>({})
  const [roleForNew,   setRoleForNew]   = useState<TrustedPersonRole>(TrustedPersonRole.PRIMARY)

  useEffect(() => { onSaveStatus('idle') }, [onSaveStatus])

  function isMinorAge(dob: string): boolean {
    if (!dob || dob.length < 10) return false
    try {
      const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000))
      return age >= 0 && age < 18
    } catch { return false }
  }

  // ── Laad bekende personen uit eerder ingevulde stappen ─────────────────────
  useEffect(() => {
    if (!userId) return

    async function loadKnown() {
      setKnownLoading(true)
      try {
        const known: KnownPerson[] = []

        // Partner
        const partner = await loadPartner(userId!)
        if (partner?.hasPartner === 'yes' && partner.name?.trim()) {
          const pd = fromPartner(partner)
          known.push({
            label: `Mijn partner - ${composeFullName(partner.name, partner.lastName)}`,
            firstName: partner.name,
            lastName: partner.lastName,
            relationship: TrustedPersonRelationship.PARTNER,
            dob:   pd.dob,
            phone: pd.phone,
            email: pd.email,
          })
        }

        // Kinderen
        const children = await loadChildren(userId!)
        if (children?.hasChildren === 'yes' && children.children?.length) {
          children.children
            .filter(c => c.name?.trim())
            .forEach(child => {
              const childLabel = child.dob && isMinorAge(child.dob)
                ? `Mijn kind - ${composeFullName(child.name, child.lastName)} ⚠️ minderjarig`
                : `Mijn kind - ${composeFullName(child.name, child.lastName)}`
              known.push({
                label:        childLabel,
                firstName:    child.name,
                lastName:     child.lastName,
                relationship: TrustedPersonRelationship.CHILD,
                dob:          child.dob ?? '',
              })
            })
        }

        setKnownPersons(known)
      } finally {
        setKnownLoading(false)
      }
    }

    loadKnown()
    reload()
    getFirstProfile().then(profile => {
      if (profile?.firstName) setOwnerName(profile.firstName)
    })
    // V35-P1-CRITICAL: laad echte geboortedatum uit wizard_personal of encryptedProfile
    loadOwnerDob(userId!).then(dob => {
      setDobHash(dob)
    })
  }, [userId])

  async function reload() {
    if (!userId) return
    setPersons(await loadTrustedPersons(userId))
  }

  // ── Prefill vanuit bekende persoon ─────────────────────────────────────────
  function handleKnownPersonSelectRaw(person: KnownPerson) {
    setForm(prev => applyKnownPerson(prev, {
      firstName:    person.firstName,
      lastName:     person.lastName,
      relationship: person.relationship,
      phone:        person.phone,
      email:        person.email,
    }, {
      firstName:    'firstName',
      lastName:     'lastName',
      relationship: 'relationship',
      phone:        'phone',
      email:        'email',
    }))
    // Geboortedatum van de VP zelf overnemen (los van eigenaar-DOB); nooit leeg overschrijven
    if (person.dob && person.dob.trim() !== '') setVpDob(person.dob)
  }

  function handleKnownPersonSelect(person: KnownPerson) {
    // V40C-P1: intercept minderjarige
    if (person.dob && isMinorAge(person.dob)) {
      setMinorName(person.firstName)
      setMinorPending(person)
      setMinorWarning(true)
      return
    }
    handleKnownPersonSelectRaw(person)
  }

  function handleMinorProceed() {
    if (minorPending) { handleKnownPersonSelectRaw(minorPending); setVpDob(minorPending.dob ?? '') }
    setMinorWarning(false); setMinorPending(null)
  }

  function handleMinorCancel() {
    setMinorWarning(false); setMinorPending(null)
    setForm(prev => ({ ...prev, firstName: '', relationship: TrustedPersonRelationship.CHILD }))
  }

  // ── Validatie ──────────────────────────────────────────────────────────────
  function validateStep() {
    const e: Partial<Form> = {}
    if (wizStep === 1) {
      if (!form.firstName.trim()) e.firstName    = 'Voornaam verplicht'
      if (!form.relationship)     e.relationship = 'Kies een relatie'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function nextWizStep() { if (!validateStep()) return; setWizStep(s => s + 1) }

  // ── Aanmaken VP ─────────────────────────────────────────────────────────────
  async function handleCreate() {
    if (!userId) return
    if (!dobHash) {
      // DOB niet beschikbaar - aanmaak blokkeren (foutmelding al zichtbaar bij knop)
      return
    }
    setIsSaving(true)
    try {
      const result = await createTrustedPerson({
        userId,
        dateOfBirth:       dobHash,
        firstName:         form.firstName,
        relationship:      form.relationship as TrustedPersonRelationship,
        email:             form.email,
        activationTrigger: form.activationTrigger as ActivationTrigger,
        role:              roleForNew,
        personDetails: {
          lastName: form.lastName,
          dob:      vpDob,            // geboortedatum VP zelf (niet eigenaar)
          phone:    form.phone,
          email:    form.email,
        },
        permissions: {
          moduleFuneralWishes:    form.moduleFuneralWishes,
          modulePersonalFarewell: form.modulePersonalFarewell,
          moduleMedicalWishes:    form.moduleMedicalWishes,
          moduleDocumentIndex:    form.moduleDocumentIndex,
          moduleMessages:         form.moduleMessages,
          moduleDigitalEstate:    form.moduleDigitalEstate,
          moduleLifeStory:        form.moduleLifeStory,
        },
      })
      setGenCode(result.displayCode)
      setWizStep(3)
      // Dashboard synchronisatie: module als actief markeren
      await saveModuleProgress(userId, 'trusted_person', 1, 1)
      onVpCreated?.()
      await reload()
    } catch {
      toast.error('Aanmaken mislukt')
    } finally {
      setIsSaving(false)
    }
  }

  function handleRevoke(id: string, name: string) {
    setConfirmRevoke({ id, name })
  }

  async function executeRevoke(id: string) {
    await revokeTrustedPerson(id)
    const remaining = await loadTrustedPersons(userId!)
    const stillActive = remaining.filter(p => p.confirmationStatus !== 'REVOKED').length
    await saveModuleProgress(userId!, 'trusted_person', stillActive, 1)
    toast.success('Verwijderd')
    setConfirmRevoke(null)
    await reload()
  }

  function handlePromote(reserveId: string, name: string) {
    setConfirmPromote({ id: reserveId, name })
  }

  async function executePromote(reserveId: string) {
    if (!userId) return
    const result = await promoteTrustedPerson(reserveId, userId)
    if (result.success) {
      toast.success(result.message)
    } else {
      toast.error(result.message)
    }
    setConfirmPromote(null)
    await reload()
  }

  function reset() {
    setForm(EMPTY_FORM)
    setVpDob('')
    setWizStep(1)
    setErrors({})
    setGenCode(null)
    setAckKeep(false)
    setAckTrust(false)
    setCopied(false)
    setShowBrief(false)
    setShowForm(false)
    onFormOpen?.(false)
  }

  function openForm(role: TrustedPersonRole) {
    setRoleForNew(role)
    setForm(EMPTY_FORM)
    setVpDob('')
    setWizStep(1)
    setErrors({})
    setShowForm(true)
    onFormOpen?.(true)
  }

  const active  = persons.filter(p => p.confirmationStatus !== 'REVOKED')
  const primary = active.find(p => p.role === TrustedPersonRole.PRIMARY)
  const reserve = active.find(p => p.role === TrustedPersonRole.RESERVE)

  // Bekende personen nog niet aangewezen als VP
  const alreadyChosen = new Set(active.map(p => p.firstName.toLowerCase()))
  const available     = knownPersons.filter(k => !alreadyChosen.has(k.firstName.toLowerCase()))

  return (
    <div className="flex flex-col gap-5">

      {/* Korte uitleg (de titel staat al in de balk bovenaan) */}
      <div>
        <p className="text-sm text-ink-3 leading-relaxed">
          U bepaalt zelf wie toegang krijgt tot de onderdelen die u deelt, en wanneer en aan wie u de sleutelcode verstrekt.
        </p>
      </div>

      {/* ── PRIMAIRE ACTIE DIRECT ZICHTBAAR - geen scrollen vereist ──────── */}

      {/* Overzicht aangewezen VPs */}
      {active.length > 0 && (
        <div className="flex flex-col gap-2">
          {[primary, reserve].filter(Boolean).map(p => p && (
            <Card key={p.id} padding="sm">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sage-50 text-sage-600 flex-shrink-0">
                  <Users className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-ink">{p.firstName}</p>
                    <Badge variant="success" size="sm">
                      {p.role === TrustedPersonRole.PRIMARY ? 'Eerste' : 'Tweede'}
                    </Badge>
                    <Badge variant="success" size="sm">Actief</Badge>
                  </div>
                  <p className="text-xs text-ink-4 mt-0.5">
                    {REL_OPTIONS.find(r => r.value === p.relationship)?.label}
                    {' · '}
                    {ACT_OPTIONS.find(a => a.value === p.activationTrigger)?.label}
                  </p>
                  <p className="text-xs text-ink-4 mt-0.5">
                    Vastgelegd: {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Overdrachtsbrief - voor beide VP-typen */}
                  <button
                    title="Overdrachtsbrief afdrukken"
                    onClick={() => setBriefForVP(p)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-sage-600 hover:bg-sage-50 transition-colors"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </button>
                  {/* Maak primair - alleen zichtbaar op de reserve-VP kaart */}
                  {p.role === TrustedPersonRole.RESERVE && (
                    <button
                      title="Maak primair vertrouwenspersoon"
                      onClick={() => handlePromote(p.id, p.firstName)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-sage-600 hover:bg-sage-50 transition-colors"
                    >
                      <ArrowUpCircle className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleRevoke(p.id, p.firstName)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Aanwijzen-wizard */}
      {showForm ? (
        <Card className="border-sage-200">
          {/* Voortgangsbalk */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex gap-1.5 flex-1 mr-4">
              {[1,2].map(s => (
                <div key={s} className={`flex-1 h-1 rounded-full transition-all ${wizStep >= s ? 'bg-moss' : 'bg-sand-300'}`} />
              ))}
            </div>
            <button onClick={reset} className="text-ink-4 hover:text-ink-3 flex-shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Stap 1 - Gegevens */}
          {wizStep === 1 && (
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-sm font-semibold text-ink mb-0.5">
                  Stap 1 van 2 - {roleForNew === TrustedPersonRole.PRIMARY ? 'Eerste vertrouwenspersoon' : 'Tweede vertrouwenspersoon'}
                </p>
                <p className="text-xs text-ink-4">Kies uw vertrouwenspersoon</p>
              </div>

              {/* V40C-P1: Minderjarige VP waarschuwingsdialoog */}
              {minorWarning && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                  onClick={() => handleMinorCancel()}>
                  <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl px-6 py-5 flex flex-col gap-4"
                    onClick={e => e.stopPropagation()}>
                    <div className="flex items-start gap-3">
                      <span className="text-xl flex-shrink-0">⚠️</span>
                      <div>
                        <p className="text-sm font-semibold text-ink mb-2">Minderjarige vertrouwenspersoon</p>
                        <p className="text-sm text-ink-3 leading-relaxed mb-2">
                          <strong>{minorName}</strong> is momenteel minderjarig.
                        </p>
                        <ul className="text-xs text-ink-4 space-y-0.5 list-disc list-inside mb-2">
                          <li>instanties informeren</li>
                          <li>documenten beheren</li>
                          <li>uitvaartzaken regelen</li>
                          <li>contact onderhouden met nabestaanden</li>
                        </ul>
                        <p className="text-xs text-ink-4 leading-relaxed">
                          Wij adviseren een volwassene als primaire vertrouwenspersoon.
                          Een minderjarige kan eventueel als <em>secundaire</em> vertrouwenspersoon worden opgenomen.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button onClick={handleMinorProceed}
                        className="w-full rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 hover:bg-amber-100 transition-colors">
                        Toch gebruiken
                      </button>
                      <button onClick={handleMinorCancel}
                        className="w-full rounded-2xl bg-moss text-white px-4 py-3 text-sm font-semibold hover:bg-moss-light transition-colors">
                        Andere persoon kiezen
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Keuzelijst - wacht op laden bekende personen (race condition fix) */}
              {knownLoading ? (
                <div className="flex items-center gap-2 rounded-xl bg-sand px-4 py-3">
                  <div className="h-4 w-4 rounded-full border-2 border-sage-400 border-t-transparent animate-spin flex-shrink-0" />
                  <p className="text-xs text-ink-4">Bekende personen laden…</p>
                </div>
              ) : available.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider">
                    Kies uit al ingevoerde personen
                  </p>
                  {available.map((person, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleKnownPersonSelect(person)}
                      className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                        form.firstName === person.firstName && form.relationship === person.relationship
                          ? 'border-sage-600 bg-sage-50'
                          : 'border-sand-300 hover:border-sage-300'
                      }`}
                    >
                      <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                        form.firstName === person.firstName && form.relationship === person.relationship
                          ? 'border-sage-600 bg-sage-600'
                          : 'border-sand-300'
                      }`} />
                      <span className="text-sm text-ink">{person.label}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, firstName: '', relationship: '' }))}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      !available.some(p => p.firstName === form.firstName)
                        ? 'border-sage-600 bg-sage-50'
                        : 'border-sand-300 hover:border-sage-300'
                    }`}
                  >
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      !available.some(p => p.firstName === form.firstName)
                        ? 'border-sage-600 bg-sage-600'
                        : 'border-sand-300'
                    }`} />
                    <span className="text-sm text-ink">Iemand anders invoeren</span>
                  </button>

                  {/* Vrije invoer bij "Iemand anders" */}
                  {!available.some(p => p.firstName === form.firstName) && (
                    <div className="border-t border-sand-200 pt-3 flex flex-col gap-3">
                      <Input
                        label="Voornaam"
                        value={form.firstName}
                        onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                        error={errors.firstName}
                        required
                        autoFocus
                      />
                      <Select
                        label="Relatie"
                        options={REL_OPTIONS}
                        value={KNOWN_REL_VALUES.has(form.relationship) ? form.relationship : (form.relationship ? TrustedPersonRelationship.OTHER : '')}
                        onChange={e => setForm(prev => ({ ...prev, relationship: e.target.value }))}
                        placeholder="Kies relatie..."
                        error={errors.relationship}
                      />
                      {/* Vrij veld bij Overig OF bij bestaande vrije tekst */}
                      {(form.relationship === TrustedPersonRelationship.OTHER ||
                        (form.relationship && !KNOWN_REL_VALUES.has(form.relationship))) && (
                        <Input
                          label="Omschrijving relatie"
                          value={KNOWN_REL_VALUES.has(form.relationship) ? '' : form.relationship}
                          onChange={e => setForm(prev => ({ ...prev, relationship: e.target.value }))}
                          placeholder="Bijv: schoonfamilie, buurvrouw..."
                        />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Geen bekende personen - direct invoervelden */
                <div className="flex flex-col gap-3">
                  <Input
                    label="Voornaam"
                    value={form.firstName}
                    onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                    error={errors.firstName}
                    required
                    autoFocus
                  />
                  <Select
                    label="Relatie"
                    options={REL_OPTIONS}
                    value={KNOWN_REL_VALUES.has(form.relationship) ? form.relationship : (form.relationship ? TrustedPersonRelationship.OTHER : '')}
                    onChange={e => setForm(prev => ({ ...prev, relationship: e.target.value }))}
                    placeholder="Kies relatie..."
                    error={errors.relationship}
                  />
                  {/* Vrij veld bij Overig OF bij bestaande vrije tekst (backward compat) */}
                  {(form.relationship === TrustedPersonRelationship.OTHER ||
                    (form.relationship && !KNOWN_REL_VALUES.has(form.relationship))) && (
                    <Input
                      label="Omschrijving relatie"
                      value={KNOWN_REL_VALUES.has(form.relationship) ? '' : form.relationship}
                      onChange={e => setForm(prev => ({ ...prev, relationship: e.target.value }))}
                      placeholder="Bijv: schoonfamilie, buurvrouw..."
                    />
                  )}
                </div>
              )}

              <Input
                label="Achternaam (optioneel)"
                value={form.lastName}
                onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))}
              />

              <Input
                label="E-mailadres (optioneel)"
                type="email"
                value={form.email}
                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                hint="Voor een jaarlijkse herinnering aan de vertrouwenspersoon"
              />

              <Input
                label="Telefoonnummer (optioneel)"
                type="tel"
                value={form.phone}
                onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
              />

              {/* Geboortedatum VP - optioneel, alleen voor leeftijdswaarschuwing */}
              <div>
                <p className="text-sm font-medium text-ink-2 mb-1.5">Geboortedatum (optioneel)</p>
                <input
                  type="date"
                  value={vpDob}
                  onChange={e => setVpDob(e.target.value)}
                  className="w-full rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-400"
                />
                <p className="text-xs text-ink-4 mt-1">Optioneel - voor een leeftijdscontrole.</p>
              </div>

              {/* Waarschuwing minderjarige vertrouwenspersoon - persistent na selectie */}
              {isMinorAge(vpDob) && (
                <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 animate-slide-down">
                  <span className="text-base flex-shrink-0">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-amber-900 mb-1">Geselecteerde vertrouwenspersoon is minderjarig</p>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      Overweeg een volwassen primaire of tweede vertrouwenspersoon.
                      Minderjarigen kunnen veel taken na overlijden niet zelfstandig uitvoeren.
                    </p>
                  </div>
                </div>
              )}

              {/* Toegangsmoment - samengevoegd op pagina 1 */}
              <div className="pt-2 border-t border-sand-200">
                <p className="text-sm font-semibold text-ink mb-0.5">Wanneer krijgt {form.firstName || 'deze persoon'} toegang?</p>
              </div>

              <RadioGroup
                name="act"
                options={ACT_OPTIONS}
                value={form.activationTrigger}
                onChange={v => setForm(prev => ({ ...prev, activationTrigger: v }))}
              />

              {/* Behoud bestaande uitklapbare uitleg */}
              <InfoBlock title="Hoe werkt dit?" variant="blue">
                <p><strong>Alleen na overlijden:</strong> de vertrouwenspersoon krijgt pas toegang nadat iemand het overlijden heeft gemeld met de sleutelcode en geboortedatum. WilsApp controleert dit niet zelf - het is een vertrouwenshandeling.</p>
                <p><strong>Direct actief:</strong> de vertrouwenspersoon krijgt nu al toegang. Handig als u ziek bent en uw wensen alvast wilt overdragen.</p>
                <p>U kunt deze keuze altijd wijzigen door de vertrouwenspersoon te verwijderen en opnieuw aan te wijzen.</p>
              </InfoBlock>

              <div className="flex gap-3">
                <Button variant="secondary" onClick={reset}>Terug</Button>
                <Button fullWidth onClick={nextWizStep}>Volgende</Button>
              </div>
            </div>
          )}

          {/* Stap 3 - Toegang instellen */}
          {wizStep === 2 && (
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-semibold text-ink mb-0.5">Stap 2 van 2 - Kies welke informatie u wilt delen</p>
                <p className="text-xs text-ink-4">Kies welke onderdelen van WilsApp u met {form.firstName || 'uw vertrouwenspersoon'} wilt delen wanneer deze persoon als vertrouwenspersoon toegang krijgt.</p>
              </div>

              <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
                <p className="text-xs text-ink-3 leading-relaxed">
                  Een vertrouwenspersoon krijgt uitsluitend toegang tot de onderdelen die u hieronder selecteert.
                  WilsApp verleent geen wettelijke bevoegdheden.
                  U bepaalt zelf wanneer en aan wie u de sleutelcode verstrekt.
                </p>
              </div>

              {/* Uitleg toegangsrechten - verduidelijking voor de gebruiker */}
              <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
                <p className="text-xs text-ink-3 leading-relaxed">
                  Alleen de <strong>hierboven aangevinkte</strong> onderdelen worden met uw vertrouwenspersoon gedeeld.
                  Andere onderdelen - zoals uw financiële gegevens en het notarisdossier - worden standaard niet met een vertrouwenspersoon gedeeld.
                  In een toekomstige versie kunnen andere bevoegde rollen (zoals een executeur of notaris) andere toegang krijgen.
                </p>
              </div>

              {/* Configureerbaar */}
              {PERMS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between rounded-xl bg-sand px-4 py-2.5">
                  <span className="text-sm text-ink-2">{label}</span>
                  <Toggle
                    label=""
                    checked={form[key as keyof Form] as boolean}
                    onChange={v => setForm(prev => ({ ...prev, [key]: v }))}
                  />
                </div>
              ))}

              {/* Nooit gedeeld */}
              <div className="mt-1">
                <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Niet zichtbaar voor de vertrouwenspersoon</p>
                {[
                  { label: 'Financieel overzicht',  reden: 'Financiële gegevens worden standaard niet met een vertrouwenspersoon gedeeld.' },
                  { label: 'Notarisdossier',         reden: 'Dit is bedoeld als persoonlijk dossier en wordt niet automatisch gedeeld.' },
                  { label: 'Persoonlijke gegevens',  reden: 'Naam en geboortedatum kan een vertrouwenspersoon nodig hebben om te weten van wie het dossier is; overige persoonsgegevens worden standaard niet gedeeld.' },
                ].map(({ label, reden }) => (
                  <div key={label} className="flex items-start gap-3 rounded-xl bg-sand border border-sand-300 px-4 py-2.5 mb-1.5 opacity-60">
                    <div className="flex-1">
                      <p className="text-sm text-ink-3">{label}</p>
                      <p className="text-xs text-ink-4 mt-0.5">{reden}</p>
                    </div>
                    <span className="text-xs text-ink-4 font-medium mt-0.5 flex-shrink-0">Niet gedeeld</span>
                  </div>
                ))}
              </div>

              {/* Sleutelcode - informatie + bevestiging (redactioneel vereenvoudigd) */}
              <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-4 mt-2 flex flex-col gap-3">
                <div>
                  <p className="text-sm font-semibold text-blue-900">🔑 Uw verantwoordelijkheid</p>
                  <p className="text-sm text-blue-900/90 leading-relaxed mt-0.5">Geef de sleutelcode uitsluitend persoonlijk aan uw vertrouwenspersoon. WilsApp verstuurt de sleutelcode nooit via e-mail, sms of WhatsApp.</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">🤝 Vertrouwenshandeling</p>
                  <p className="text-sm text-blue-900/90 leading-relaxed mt-0.5">WilsApp controleert geen overlijden. Geef de sleutelcode alleen aan iemand die u volledig vertrouwt en spreek samen duidelijk af wanneer deze gebruikt mag worden.</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">🗄️ Zorgvuldig bewaren</p>
                  <p className="text-sm text-blue-900/90 leading-relaxed mt-0.5">Bewaar de sleutelcode op een veilige plaats. Vermoedt u dat de code bekend is geworden bij onbevoegden? Maak dan direct een nieuwe sleutelcode aan.</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">♻️ Nieuwe sleutelcode</p>
                  <p className="text-sm text-blue-900/90 leading-relaxed mt-0.5">Wanneer u een nieuwe sleutelcode aanmaakt, vervalt de vorige automatisch.</p>
                </div>
                <div className="flex flex-col gap-2 border-t border-blue-200 pt-3">
                  <Checkbox checked={ackKeep} onChange={e => setAckKeep(e.target.checked)}
                    label="Ik geef de sleutelcode uitsluitend persoonlijk aan mijn vertrouwenspersoon." />
                  <Checkbox checked={ackTrust} onChange={e => setAckTrust(e.target.checked)}
                    label="Ik begrijp dat WilsApp geen overlijden controleert en dat het gebruik van de sleutelcode gebaseerd is op vertrouwen." />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="secondary" onClick={() => setWizStep(1)}>Terug</Button>
                <Button fullWidth onClick={handleCreate} loading={isSaving}
                  disabled={!dobHash || !ackKeep || !ackTrust}>
                  Sleutelcode aanmaken
                </Button>
              </div>
              {!dobHash && (
                <p className="text-xs text-status-alertText mt-1">
                  Vul eerst uw geboortedatum in bij{' '}
                  <strong>Persoonlijke gegevens</strong>.
                  Deze is nodig voor een veilige toegangscode voor uw vertrouwenspersoon.
                </p>
              )}
            </div>
          )}

          {/* Stap 4 - Sleutelcode */}
          {wizStep === 3 && genCode && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center text-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-moss">
                  <ShieldCheck className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-base font-semibold text-ink mb-1">
                    Sleutelcode voor {form.firstName}
                  </p>
                  <p className="text-xs text-ink-4">
                    {roleForNew === TrustedPersonRole.PRIMARY ? 'Eerste vertrouwenspersoon' : 'Tweede vertrouwenspersoon'}
                  </p>
                </div>
              </div>

              {/* De code */}
              <div className="flex flex-col items-center gap-3">
                <div className="text-3xl font-mono font-bold text-ink tracking-widest px-8 py-4 bg-sage-50 rounded-2xl border-2 border-sage-200">
                  {genCode}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(genCode.replace('–', '-'))
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                  className="flex items-center gap-1.5 text-sm text-sage-700 hover:text-sage-800 transition-colors"
                >
                  {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Gekopieerd' : 'Code kopiëren'}
                </button>
              </div>

              {/* InfoBlock bij sleutelcode - alleen de vervolgactie (uitleg staat eerder) */}
              <InfoBlock title="De volgende stap" defaultOpen={true} variant="info">
                <p><strong>Geef de code nu persoonlijk aan {form.firstName}</strong> - op papier of mondeling - en leg kort uit wat WilsApp is en wanneer de code gebruikt mag worden.</p>
                <p><strong>Bewaar zelf een notitie</strong> waar u de code heeft bewaard.</p>
              </InfoBlock>

              {/* Overdrachtsbrief knop */}
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowBrief(true)}
              >
                Overdrachtsbrief afdrukken
              </Button>

              <Button fullWidth onClick={reset}>Klaar</Button>

              {/* Printbare overdrachtsbrief modal */}
              {showBrief && (
                <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto print:bg-white print:p-0 print:block print:inset-auto print:fixed-auto">
                  <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl print:shadow-none print:rounded-none print:max-w-none">
                    {/* Print-stijlen ingebakken */}
                    <style>{`
                      @media print {
                        body > *:not(.wilsapp-brief-wrapper) { display: none !important; }
                        .wilsapp-brief-wrapper { display: block !important; position: fixed; top: 0; left: 0; width: 100%; }
                        .no-print { display: none !important; }
                        .brief-content { padding: 40px !important; font-size: 14px !important; }
                      }
                    `}</style>

                    {/* Scherm-header */}
                    <div className="no-print flex items-center justify-between px-5 py-4 border-b border-sand-200">
                      <p className="text-sm font-semibold text-ink">Overdrachtsbrief vertrouwenspersoon</p>
                      <button
                        onClick={() => setShowBrief(false)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-ink-4 hover:bg-sand-100"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Brief inhoud */}
                    <div className="brief-content px-8 py-6 text-sm text-ink leading-relaxed flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                        <p className="text-xs text-ink-4">{new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        <p className="text-lg font-semibold text-ink">Aan: {form.firstName}</p>
                      </div>

                      <p className="text-ink-2 leading-relaxed">
                        Ik heb mijn wensen en instructies vastgelegd in WilsApp, een hulpmiddel voor nabestaanden.
                        Met de onderstaande sleutelcode en mijn geboortedatum kunt u deze informatie inzien wanneer dat nodig is.
                      </p>

                      {/* Sleutelcode - prominent */}
                      <div className="bg-sage-50 border-2 border-sage-300 rounded-2xl px-6 py-5 flex flex-col items-center gap-2 text-center">
                        <p className="text-xs font-semibold text-sage-600 uppercase tracking-wider">Uw sleutelcode</p>
                        <p className="text-3xl font-mono font-bold text-ink tracking-widest">{genCode}</p>
                        <p className="text-xs text-ink-4">Voor toegang is óók mijn geboortedatum nodig (bij u bekend). Die staat om veiligheidsredenen niet op deze brief.</p>
                      </div>

                      {/* Instructies */}
                      <div className="flex flex-col gap-2">
                        <p className="text-sm font-semibold text-ink">Hoe gebruikt u deze code?</p>
                        <ol className="flex flex-col gap-1 text-sm text-ink-2 list-decimal list-inside">
                          <li>Ga naar <strong>wilsapp.nl/toegang</strong></li>
                          <li>Voer de sleutelcode in: <strong>{genCode}</strong></li>
                          <li>Voer mijn geboortedatum in (die is bij u bekend)</li>
                        </ol>
                      </div>

                      {/* Activatiemoment */}
                      <div className="bg-sand rounded-xl px-4 py-3">
                        <p className="text-xs font-semibold text-ink mb-1">Wanneer te gebruiken</p>
                        <p className="text-sm text-ink-2">
                          {form.activationTrigger === 'IMMEDIATE'
                            ? 'U kunt deze code direct gebruiken.'
                            : 'Gebruik deze code na mijn overlijden.'}
                        </p>
                      </div>

                      {/* Bewaarinstructie */}
                      <div className="border-t border-sand-200 pt-4">
                        <p className="text-xs font-semibold text-ink mb-1">Bewaarinstructie</p>
                        <p className="text-sm text-ink-2">
                          Bewaar deze brief op een veilige, persoonlijke plek. Geef de code niet door aan anderen.
                          Als de code verloren gaat, kan een nieuwe worden aangemaakt via WilsApp.
                        </p>
                        <p className="text-sm text-ink-2 mt-2">
                          <strong>Let op:</strong> WilsApp controleert niet of iemand is overleden. Het gebruik van deze code is een vertrouwenshandeling.
                        </p>
                      </div>

                      {/* Afzender */}
                      <div className="border-t border-sand-200 pt-4 flex flex-col gap-0.5">
                        <p className="text-sm font-semibold text-ink">{ownerName || 'Uw naaste'}</p>
                        <p className="text-xs text-ink-4">Opgesteld via WilsApp - wilsapp.nl</p>
                        <p className="text-xs text-ink-4">Datum: {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      </div>
                    </div>

                    {/* Afdruk-knop */}
                    <div className="no-print px-8 pb-6 flex gap-3">
                      <Button variant="secondary" onClick={() => setShowBrief(false)} fullWidth>
                        Sluiten
                      </Button>
                      <Button fullWidth onClick={() => window.print()}>
                        Afdrukken
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      ) : (
        /* Knoppen om wizard te openen */
        <div className="flex flex-col gap-3">
          {!primary && (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => openForm(TrustedPersonRole.PRIMARY)}
                className="flex items-center gap-4 rounded-2xl bg-moss text-white px-5 py-5 text-left shadow-card hover:bg-moss-light active:scale-[0.98] transition-all"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-base font-semibold text-white">Vertrouwenspersoon aanwijzen</p>
                  <p className="text-xs text-white/75 mt-0.5">
                    Kies wie toegang krijgt tot de onderdelen die u deelt
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-white/70 flex-shrink-0" />
              </button>
              {/* Later instellen - volwaardige secundaire knop */}
              {!laterChosen ? (
                <button
                  type="button"
                  onClick={() => { setLaterChosen(true); onLaterChosen?.() }}
                  className="flex items-center justify-center gap-2 rounded-2xl border-2 border-sage-300 bg-white px-5 py-4 text-base font-semibold text-sage-700 hover:border-sage-500 hover:bg-sage-50 active:scale-[0.98] transition-all w-full"
                >
                  Later instellen
                </button>
              ) : (
                <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
                  <span className="text-base flex-shrink-0">ℹ️</span>
                  <p className="text-xs text-sage-800 leading-relaxed">
                    U kunt later een vertrouwenspersoon aanwijzen via het menu.
                    De knop <strong>Volgende</strong> is nu actief.
                  </p>
                </div>
              )}
            </div>
          )}
          {primary && !reserve && (
            <div className="flex flex-col gap-3">
              <div className="rounded-xl border border-sand-300 bg-white px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-ink-3 uppercase tracking-wider mb-0.5">Tweede vertrouwenspersoon</p>
                  <p className="text-sm text-ink-4 italic">Niet ingesteld</p>
                </div>
                <span className="text-xs text-amber-600 font-medium bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">Aanbevolen</span>
              </div>
              <div className="rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
                <p className="text-xs font-semibold text-sage-700 mb-1">Waarom een tweede vertrouwenspersoon?</p>
                <p className="text-xs text-sage-600 leading-relaxed">
                  Een tweede vertrouwenspersoon biedt extra zekerheid wanneer {primary.firstName} niet
                  beschikbaar is of gelijktijdig door een calamiteit is getroffen. Beide personen zijn
                  gelijkwaardig - de tweede wordt alleen gebruikt als de eerste niet bereikbaar is.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => openForm(TrustedPersonRole.RESERVE)}
                icon={<Users className="h-4 w-4" />}
              >
                Tweede vertrouwenspersoon toevoegen
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Uitleg - zichtbaar NADAT gebruiker de primaire actie heeft gezien */}
      {!showForm && (
        <InfoBlock title="Goed om te weten - vertrouwenspersoon" variant="blue" defaultOpen={false}>
          <p>In de praktijk lopen nabestaanden vaak vast op één vraag: wie mag dit eigenlijk regelen? Met een vertrouwenspersoon is dat van tevoren beslist. Zij kunnen, met de door u verstrekte sleutelcode, uw vastgelegde wensen en praktische informatie in WilsApp raadplegen wanneer dat nodig is.</p>
          <p>Mijn advies: wijs er liefst twee aan. Een <strong>tweede vertrouwenspersoon</strong> geeft extra zekerheid wanneer de eerste niet beschikbaar is, of tegelijk door dezelfde gebeurtenis is getroffen. Beide personen zijn gelijkwaardig. Laat ze ook even weten dat u hen heeft gekozen.</p>
          <p>Een vertrouwenspersoon is <strong>geen executeur</strong>, <strong>geen wettelijk vertegenwoordiger</strong> en krijgt <strong>geen notariële bevoegdheden</strong>. Hij of zij krijgt uitsluitend toegang tot de informatie die u zelf binnen WilsApp heeft vastgelegd - nooit uw financiële of persoonsgegevens.</p>
          <p className="text-xs text-ink-3 mt-2 leading-relaxed">
            <strong>Let op:</strong> dit is geen wettelijke vertegenwoordiging. Voor financiële volmacht of medische beslissingen is een{' '}
            <a href="https://www.knb.nl/kennisbank/familie-erfrecht/levenstestament/" target="_blank" rel="noopener noreferrer"
               className="text-sage-600 underline underline-offset-2 hover:text-sage-800">
              levenstestament of volmacht via de notaris
            </a>
            {' '}nodig.
          </p>
        </InfoBlock>
      )}

      {/* Bevestiging onderaan als alles ingesteld */}
      {active.length > 0 && !showForm && (
        <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-100 px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-sage-700 leading-relaxed">
            {primary && reserve
              ? `${primary.firstName} (eerste) en ${reserve.firstName} (tweede) hebben elk een unieke sleutelcode ontvangen.`
              : `${primary?.firstName} heeft een sleutelcode ontvangen. U kunt ook een tweede vertrouwenspersoon aanwijzen als vangnet.`
            }
          </p>
        </div>
      )}

      {/* Inline bevestiging: verwijderen */}
      {confirmRevoke && (
        <ConfirmDialog
          message={`Weet u zeker dat u ${confirmRevoke.name} als vertrouwenspersoon wilt verwijderen? De sleutelcode wordt ongeldig.`}
          onConfirm={() => executeRevoke(confirmRevoke.id)}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}

      {/* Inline bevestiging: promoveren */}
      {confirmPromote && (
        <ConfirmDialog
          message={`${confirmPromote.name} promoveren tot primaire vertrouwenspersoon? De huidige primaire VP wordt dan secundair.`}
          onConfirm={() => executePromote(confirmPromote.id)}
          onCancel={() => setConfirmPromote(null)}
        />
      )}

      {/* V41: Overdrachtsbrief voor bestaande VP (zonder toegangscode - code is al overhandigd) */}
      {briefForVP && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto print:bg-white print:p-0 print:block">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl print:shadow-none print:rounded-none print:max-w-none">
            <style>{`@media print { body > *:not(.wilsapp-brief-wrapper) { display: none !important; } .wilsapp-brief-wrapper { display: block !important; position: fixed; top: 0; left: 0; width: 100%; } .no-print { display: none !important; } .brief-content { padding: 40px !important; font-size: 14px !important; } }`}</style>
            <div className="wilsapp-brief-wrapper">
              <div className="no-print flex items-center justify-between px-6 pt-5 pb-2">
                <p className="text-sm font-semibold text-ink">Overdrachtsbrief - {briefForVP.firstName}</p>
                <button onClick={() => setBriefForVP(null)} className="text-ink-4 hover:text-ink-3"><X className="h-4 w-4" /></button>
              </div>
              <div className="brief-content px-8 py-6 text-sm text-ink leading-relaxed flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-ink-4">{new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <p className="text-lg font-semibold text-ink">Aan: {briefForVP.firstName}</p>
                  <p className="text-xs text-ink-4">
                    Rol: {briefForVP.role === TrustedPersonRole.PRIMARY ? 'Eerste vertrouwenspersoon' : 'Tweede vertrouwenspersoon'}
                  </p>
                </div>
                <p className="text-ink-2 leading-relaxed">
                  Ik heb mijn wensen en instructies vastgelegd in WilsApp, een hulpmiddel voor nabestaanden.
                  U heeft eerder een sleutelcode van mij ontvangen. Met die code en mijn geboortedatum kunt u deze informatie inzien wanneer dat nodig is.
                </p>
                <div className="bg-sand rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-ink mb-1">Hoe gebruikt u uw sleutelcode?</p>
                  <ol className="flex flex-col gap-1 text-sm text-ink-2 list-decimal list-inside">
                    <li>Ga naar <strong>wilsapp.nl/toegang</strong></li>
                    <li>Voer uw sleutelcode in (die u eerder van mij heeft ontvangen)</li>
                    <li>Voer mijn geboortedatum in</li>
                  </ol>
                </div>
                <div className="bg-sand rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-ink mb-1">Wanneer te gebruiken</p>
                  <p className="text-sm text-ink-2">
                    {briefForVP.activationTrigger === 'IMMEDIATE'
                      ? 'U kunt uw sleutelcode direct gebruiken.'
                      : 'Gebruik uw sleutelcode na mijn overlijden.'}
                  </p>
                </div>
                <div className="border-t border-sand-200 pt-4">
                  <p className="text-xs font-semibold text-ink mb-1">Bewaarinstructie</p>
                  <p className="text-sm text-ink-2">Bewaar deze brief op een veilige, persoonlijke plek. Geef de code niet door aan anderen.</p>
                </div>
                <div className="border-t border-sand-200 pt-4 flex flex-col gap-0.5">
                  <p className="text-sm font-semibold text-ink">{ownerName || 'Uw naaste'}</p>
                  <p className="text-xs text-ink-4">Opgesteld via WilsApp - wilsapp.nl</p>
                </div>
              </div>
              <div className="no-print px-8 pb-6 flex gap-3">
                <Button variant="secondary" onClick={() => setBriefForVP(null)} fullWidth>Sluiten</Button>
                <Button fullWidth onClick={() => window.print()}>Afdrukken</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
